/**
 * "Is this person talking?" from audio levels polled ~10 times a second. Hysteresis keeps
 * the speaking marks calm: a voice must be above ON_DB for a couple of polls to light the
 * mark, and below OFF_DB for a while (the pauses between words) to switch it off.
 */

export interface SpeakingThresholds {
  /** Level (dBFS) that counts as talking. */
  onDb: number;
  /** Level (dBFS) under which the voice counts as quiet; below onDb. */
  offDb: number;
  /** Consecutive loud polls to switch on. */
  onPolls: number;
  /** Consecutive quiet polls to switch off. */
  offPolls: number;
}

/**
 * Speech after the browser's gain control sits around -30..-20 dBFS, a quiet room after its
 * noise suppression well below -55. At 10 polls/s: on after 0.2 s, off after 0.6 s.
 */
export const SPEAKING_THRESHOLDS: Readonly<SpeakingThresholds> = {
  onDb: -45,
  offDb: -52,
  onPolls: 2,
  offPolls: 6,
};

export class SpeakingDetector {
  private speaking = false;
  private streak = 0;

  constructor(private readonly thresholds: Readonly<SpeakingThresholds> = SPEAKING_THRESHOLDS) {}

  get value(): boolean {
    return this.speaking;
  }

  /** Feeds one level measurement (dBFS, -Infinity for silence); returns the new state. */
  update(levelDb: number): boolean {
    const { onDb, offDb, onPolls, offPolls } = this.thresholds;
    if (this.speaking) {
      this.streak = levelDb < offDb ? this.streak + 1 : 0;
      if (this.streak >= offPolls) {
        this.speaking = false;
        this.streak = 0;
      }
    } else {
      this.streak = levelDb >= onDb ? this.streak + 1 : 0;
      if (this.streak >= onPolls) {
        this.speaking = true;
        this.streak = 0;
      }
    }
    return this.speaking;
  }

  reset(): void {
    this.speaking = false;
    this.streak = 0;
  }
}
